import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import BridgeClubLayout from './BridgeClubLayout';
import { deliveryAPI, DeliveryPage as PageData, DeliveryPageBin, PropertyData } from '../../services/deliveryAPI';
import FeedProductPicker, { FeedSelection, emptyFeedSelection } from './feed/FeedProductPicker';
import { hasFeedChoice } from './feed/resolveFeedProduct';
import RideAlongDialog from './RideAlongDialog';
import { shouldShowFormType } from './feed/feedConstants';
import ZoomImage from './feed/ZoomImage';
import type { RideAlong } from '../../services/deliveryAPI';

/**
 * The customer's Delivery page - one tap from the dashboard card (Brad, 2026-09-18).
 *
 * Everything about their subscription, no email needed: the next delivery with prices and
 * THE CHARGE DATE, change bag counts, add a bag of any bin they've asked us to keep filled,
 * skip; their bins and when each is due; delivery day, where the bags go, the card on file;
 * the punch card; past deliveries. Guards come back from the server as plain sentences and
 * are shown as such. After the charge date the order is locked and the page says so and why.
 */

const RED = '#8B0000';
const fmtDay = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
};
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const money = (v: string | null | undefined) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);

const card: React.CSSProperties = { background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '14px 16px', marginBottom: 12 };
const h3: React.CSSProperties = { margin: '0 0 8px', fontSize: 17, color: RED };
// THUMB TARGETS (Brad, 2026-09-18): every tap on this page is at least 44 px tall - the
// steppers are square, the buttons and inputs carry a min height. Nothing desktop-sized.
const stepper: React.CSSProperties = { width: 44, height: 44, borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontSize: 22, fontWeight: 700, cursor: 'pointer', lineHeight: 1, flexShrink: 0 };
const primary: React.CSSProperties = { minHeight: 44, padding: '0 16px', borderRadius: 8, border: 'none', background: RED, color: '#fff', fontWeight: 700, cursor: 'pointer' };
const secondary: React.CSSProperties = { minHeight: 44, padding: '0 16px', borderRadius: 8, border: `1px solid ${RED}`, background: '#fff', color: RED, fontWeight: 600, cursor: 'pointer' };
const muted: React.CSSProperties = { color: '#6b7280', fontSize: 13 };
// A text link that is still a 44 px-tall thumb target.
const tapLink: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', minHeight: 44, textDecoration: 'none' };

const DROP_LABEL: Record<string, string> = { front_door: 'Front door', back_door: 'Back door', garage: 'Garage', other: 'Somewhere else (photo)' };
const DROP_OPTIONS = ['front_door', 'back_door', 'garage', 'other'] as const;

const DeliveryPage: React.FC = () => {
  const [data, setData] = useState<PageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);   // a guard, in a sentence
  const [garageCode, setGarageCode] = useState('');
  const [savingDrop, setSavingDrop] = useState(false);
  // "Change what we deliver": the bin being changed, and the pick so far.
  const [changing, setChanging] = useState<DeliveryPageBin | null>(null);
  const [feedSel, setFeedSel] = useState<FeedSelection>(emptyFeedSelection);
  const [savingFeed, setSavingFeed] = useState(false);
  const [feedError, setFeedError] = useState<string | null>(null);
  // "Also bring": the ride-along dialog - adding (null) or changing one (the item).
  const [rideAlong, setRideAlong] = useState<RideAlong | null | undefined>(undefined);
  // The T-10 email's "Also bring something" lands here with ?also=1 and opens the dialog.
  const location = useLocation();

  useEffect(() => {
    let alive = true;
    deliveryAPI.getDeliveryPage()
      .then((d) => {
        if (!alive) return;
        setData(d);
        const params = new URLSearchParams(location.search);
        if (d && params.get('also')) setRideAlong(null);
        // ?change=<bin id>: the Feed page's "Change what we deliver" lands with the
        // dialog already open on that bin (Brad, 2026-09-22: "The user is already
        // wanting the change. Just push them forward further.").
        const changeId = Number(params.get('change'));
        const target = d && changeId ? d.bins.find((b) => b.bin_id === changeId) : undefined;
        if (target) { setChanging(target); setFeedSel({ ...emptyFeedSelection, formType: target.form_type || 'pellet' }); setFeedError(null); }
      })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const run = async (action: () => Promise<PageData>) => {
    setBusy(true); setNotice(null);
    try { setData(await action()); }
    catch (err: any) { setNotice(err?.error || err?.detail || "That didn't save. Please try again."); }
    finally { setBusy(false); }
  };

  // Pick a feed for the NEXT delivery. Nothing is added to the bin - that is the
  // whole point (Brad, 2026-09-22). A custom "Other" entry cannot be delivered, so it
  // is refused here in words rather than saved as a bin we can never fill.
  const saveFeedChange = async () => {
    if (!changing) return;
    if (feedSel.showOtherInput || !hasFeedChoice(feedSel)) {
      setFeedError("Pick a feed from the list. We can only deliver what's in the catalog.");
      return;
    }
    setSavingFeed(true); setFeedError(null);
    try {
      // The form picked here reaches the bin: it decides the bag and the price.
      const form = shouldShowFormType(changing.feed_category || 'dog') ? { form_type: feedSel.formType } : {};
      const page = await deliveryAPI.changeBinFeed(changing.bin_id, feedSel.catalogProductId
        ? { catalog_product_id: feedSel.catalogProductId, ...form }
        : { feed_product_id: feedSel.selectedProductId!, ...form });
      setData(page);
      setChanging(null);
      setFeedSel(emptyFeedSelection);
      if (page.notice) setNotice(page.notice);
    } catch (err: any) {
      setFeedError(err?.error || err?.detail || "That didn't save. Please try again.");
    } finally { setSavingFeed(false); }
  };

  const saveDrop = async (value: PropertyData['drop_location'], code?: string) => {
    setSavingDrop(true); setNotice(null);
    try {
      const body: PropertyData = { drop_location: value };
      if (value === 'garage' && code !== undefined) body.garage_code = code;
      await deliveryAPI.saveProperty(body);
      setData(await deliveryAPI.getDeliveryPage());
    } catch (err: any) { setNotice(err?.error || err?.detail || "That didn't save. Please try again."); }
    finally { setSavingDrop(false); }
  };

  if (loading) return <BridgeClubLayout><div style={{ padding: 16 }}>Loading your delivery…</div></BridgeClubLayout>;
  if (!data) {
    return (
      <BridgeClubLayout>
        <div style={{ padding: 16 }}>
          <p>We couldn't find a delivery subscription for this account.</p>
          <Link to="/bridge-club/dashboard" style={{ color: RED, fontWeight: 700 }}>Back to home</Link>
        </div>
      </BridgeClubLayout>
    );
  }

  const order = data.order;
  const open = data.state === 'open';
  const phoneHref = `tel:${(data.phone || '').replace(/[^0-9]/g, '')}`;

  const nextDelivery = () => {
    switch (data.state) {
      case 'setup_incomplete':
        return <p>Finish setting up your delivery{data.next_step_label ? `. Next: ${data.next_step_label.toLowerCase()}.` : '.'} <Link to="/bridge-club/onboarding" style={{ color: RED, fontWeight: 700 }}>Continue setup →</Link></p>;
      case 'awaiting_launch':
      case 'awaiting_activation':
        return <p>{data.launch_line}</p>;
      case 'paused':
        return <p>Your deliveries are paused. Call us at <a href={phoneHref} style={{ color: RED }}>{data.phone}</a> to start again.</p>;
      case 'cancelled':
        return <p>Your delivery subscription is cancelled. Call us at <a href={phoneHref} style={{ color: RED }}>{data.phone}</a> if you'd like to come back.</p>;
      case 'today':
        return <p style={{ fontWeight: 600 }}>Your delivery is on today's route.</p>;
      case 'en_route':
        return <p style={{ fontWeight: 600 }}>Your delivery is on the way today.</p>;
      case 'delivered':
        return <p style={{ fontWeight: 600 }}>Delivered{data.stop ? ` ${fmtDay(data.stop.delivery_date)}` : ''}. Thank you!</p>;
      case 'payment_failed':
        return <p>Your card was declined for the {order ? fmtDay(order.delivery_date) : 'next'} delivery, so it isn't bought yet. Call us at <a href={phoneHref} style={{ color: RED, fontWeight: 700 }}>{data.phone}</a> and we'll sort it out.</p>;
      case 'waiting':
        if (order && order.status === 'skipped') return <p>You skipped your {fmtDay(order.delivery_date)} delivery. We'll build your next one from your bins.</p>;
        if (order) return <p>Your next delivery is {fmtDay(order.delivery_date)}. You can change it from {fmtDay(order.window.opens)}, and we'll email you then.</p>;
        return <p>We'll email you about ten days before your next delivery, when your bins are due for feed.</p>;
      default:
        return null;
    }
  };

  const showLines = order && (open || data.state === 'locked');

  return (
    <BridgeClubLayout>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '12px 16px 32px' }}>
        {/* Back goes where they came from: the Feed page when it sent them here. */}
        {new URLSearchParams(location.search).get('from') === 'feed'
          ? <Link to="/bridge-club/feed" style={{ ...muted, ...tapLink, fontSize: 14 }}>‹ Feed</Link>
          : <Link to="/bridge-club/dashboard" style={{ ...muted, ...tapLink, fontSize: 14 }}>‹ Home</Link>}
        <h2 style={{ color: RED, margin: '6px 0 12px' }}>Your subscription</h2>

        {notice && (
          <div style={{ color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>{notice}</div>
        )}

        {/* ── Next delivery ─────────────────────────────────────────────── */}
        <div style={card}>
          <h3 style={h3}>Next delivery</h3>
          {nextDelivery()}
          {showLines && order && (
            <>
              <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
                {open ? `Here's what's coming ${fmtDay(order.delivery_date)}.` : `Confirmed for ${fmtDay(order.delivery_date)}.`}
              </p>
              {order.lines.map((line) => (
                <div key={line.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid #f3f4f6' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ overflowWrap: 'anywhere' }}>{line.label}{line.bin_name ? <span style={muted}> · {line.bin_name}</span> : null}</div>
                    <div style={muted}>{money(line.unit_price)} each{line.bag_lbs ? ` · ${Number(line.bag_lbs)} lb` : ''}{line.milled ? ' · Red Bridge feed' : ''}</div>
                  </div>
                  {open ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button style={stepper} disabled={busy} aria-label={`One fewer ${line.label}`}
                        onClick={() => run(() => deliveryAPI.setOrderBagCount(order.id, line.id, line.bag_count - 1) as Promise<PageData>)}>−</button>
                      <span style={{ minWidth: 26, textAlign: 'center', fontWeight: 700 }}>{line.bag_count}</span>
                      <button style={stepper} disabled={busy} aria-label={`One more ${line.label}`}
                        onClick={() => run(() => deliveryAPI.setOrderBagCount(order.id, line.id, line.bag_count + 1) as Promise<PageData>)}>+</button>
                    </div>
                  ) : (
                    <span style={{ fontWeight: 700 }}>{line.bag_count} bag{line.bag_count === 1 ? '' : 's'}</span>
                  )}
                  <span style={{ minWidth: 64, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(line.line_total)}</span>
                </div>
              ))}
              {open && order.addable_bins.length > 0 && (
                <div style={{ borderTop: '1px solid #f3f4f6', padding: '8px 0' }}>
                  <div style={{ ...muted, marginBottom: 6 }}>Add a bag from another bin:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {order.addable_bins.map((b) => (
                      <button key={b.bin_id} style={secondary} disabled={busy}
                        onClick={() => run(() => deliveryAPI.addOrderLine(order.id, b.bin_id))}>
                        + {b.feed || b.name}{b.price ? ` (${money(b.price)})` : ''}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 6, paddingTop: 8, fontSize: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Feed</span><span>{money(order.subtotal)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Tax</span><span>{money(order.tax)}</span></div>
                {order.fee && Number(order.fee) > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{data.tier_label || 'Service'}</span><span>{money(order.fee)}</span></div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: 4 }}>
                  <span>{order.charged ? 'Charged' : 'Estimated total'}</span><span>{money(order.charged_total || order.estimated_total)}</span>
                </div>
              </div>
              {open ? (
                <>
                  <p style={{ margin: '10px 0', fontSize: 14, color: '#374151' }}>
                    Change or skip by <strong>{fmtDay(order.window.last_day)}</strong>. Your card is charged on <strong>{fmtDay(order.charge_date)}</strong>, and we'll email you exactly what you'll pay.
                  </p>
                  <button style={secondary} disabled={busy}
                    onClick={() => { if (window.confirm(`Skip your ${fmtDay(order.delivery_date)} delivery? Nothing will be charged or delivered.`)) run(() => deliveryAPI.skipOrder(order.id) as Promise<PageData>); }}>
                    Skip this delivery
                  </button>
                </>
              ) : (
                <p style={{ margin: '10px 0 0', fontSize: 14, color: '#374151' }}>
                  {data.locked_line || `This order is confirmed and can't be changed now. Call us at ${data.phone} if something's wrong.`}
                </p>
              )}
            </>
          )}
        </div>

        {/* ── Bins ──────────────────────────────────────────────────────────
            ONE CARD PER BIN, FEW WORDS, EACH WITH A JOB (Brad, 2026-09-22: "With
            multiple bins, its a lot of words and nothing well defined"). Name and a
            due pill on top; the feed with its picture; the level; one line of
            delivery status; the receipt only when it names a DIFFERENT feed; one
            button. */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <h3 style={h3}>Your bins</h3>
            <Link to="/bridge-club/feed" style={{ ...tapLink, color: RED, fontSize: 14, fontWeight: 600 }}>Manage bins →</Link>
          </div>
          {data.bins.length === 0 && <p style={muted}>No bins yet. Add one under Feed.</p>}
          {data.bins.map((b) => {
            const days = b.days_to_due != null ? Math.round(Number(b.days_to_due)) : null;
            const pill: React.CSSProperties = {
              fontSize: 13, fontWeight: 700, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap',
              background: days != null && days <= 7 ? '#fef3c7' : '#f3f4f6',
              color: days != null && days <= 7 ? '#92400e' : '#374151',
            };
            return (
              <div key={b.bin_id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 12px 10px', marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 17, overflowWrap: 'anywhere' }}>{b.name}</div>
                  {days != null && <span style={pill}>{days <= 0 ? 'Due now' : `Due in ${days} day${days === 1 ? '' : 's'}`}</span>}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: b.image_url ? '48px 1fr' : '1fr', columnGap: 10, alignItems: 'start', marginTop: 8 }}>
                  {b.image_url && <ZoomImage src={b.image_url} size={48} />}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ ...muted, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>We deliver</div>
                    {b.feed
                      ? <div style={{ fontWeight: 600, fontSize: 16, lineHeight: 1.3, overflowWrap: 'anywhere' }}>{b.feed}</div>
                      : <div style={{ color: '#b45309', fontWeight: 600, fontSize: 15 }}>No delivery feed chosen</div>}
                    {(b.bag_lbs || b.price || b.form_type) && (
                      <div style={{ ...muted, fontSize: 14, marginTop: 2 }}>
                        {[b.bag_lbs ? `${Number(b.bag_lbs)} lb bag` : null, b.price ? money(b.price) : null,
                          b.form_type && b.form_type !== 'kibble' && b.form_type !== 'other' ? b.form_type : null]
                          .filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                </div>

                {(b.current_lbs != null || (b.daily_lbs != null && Number(b.daily_lbs) > 0)) && (
                  <div style={{ ...muted, fontSize: 14, marginTop: 8 }}>
                    {[b.current_lbs != null ? `${Number(b.current_lbs).toFixed(0)} lb in the bin` : null,
                      b.daily_lbs != null && Number(b.daily_lbs) > 0 ? `${Number(b.daily_lbs).toFixed(1)} lb a day` : null]
                      .filter(Boolean).join(' · ')}
                  </div>
                )}

                <div style={{ fontSize: 14, marginTop: 6, fontWeight: 600,
                              color: b.selected ? '#166534' : b.deliverable ? '#6b7280' : '#92400e' }}>
                  {b.selected ? '✓ We keep this one filled' : b.deliverable ? 'Not on your delivery' : (b.reason || 'Not deliverable yet')}
                </div>
                {b.selected && b.animals === 0 && (
                  <div style={{ ...muted, fontSize: 14 }}>Add an animal so we know when it needs filling.</div>
                )}

                {/* The receipt, ONLY when it names a different feed (or there is none). */}
                {b.last_added && (b.feed_differs || !b.feed) && (
                  <div style={{ fontSize: 14, marginTop: 6, color: '#92400e', background: '#fffbeb', borderRadius: 8, padding: '8px 10px' }}>
                    Last receipt: <strong>{b.last_added}</strong>.{' '}
                    {b.feed
                      ? <>Not what we deliver here. <Link to="/bridge-club/feed" style={{ color: RED, fontWeight: 600 }}>Switch under Feed →</Link></>
                      : <><Link to="/bridge-club/feed" style={{ color: RED, fontWeight: 600 }}>Choose the delivery feed under Feed →</Link></>}
                  </div>
                )}

                {/* THE MISSING VERB (Brad, 2026-09-22): change what the NEXT delivery
                    brings, without pretending a bag was bought. */}
                <button
                  type="button"
                  disabled={busy || savingFeed}
                  onClick={() => { setChanging(b); setFeedSel({ ...emptyFeedSelection, formType: b.form_type || 'pellet' }); setFeedError(null); }}
                  style={{ ...secondary, width: '100%', marginTop: 10 }}
                >
                  Change what we deliver
                </button>
              </div>
            );
          })}
          <p style={{ ...muted, marginTop: 4 }}>Every delivery carries at least one bag of Red Bridge feed. Pet food and other brands ride along with it.</p>
        </div>

        {/* ── Also bring ─────────────────────────────────────────────────
            RIDE-ALONGS (Brad, 2026-09-22): wild bird seed, litter, a chew - things we
            deliver with the feed that live in no bin. A bin predicts when feed runs
            out from the animals eating it; nobody counts the birds at a feeder. So
            these have no bin, no rate and no due date: they come with the next feed
            delivery, once or every Nth time, and never make a trip on their own. */}
        <div style={card}>
          <h3 style={h3}>Also bring</h3>
          {(data.ride_alongs || []).length === 0 && (
            <p style={muted}>Wild bird seed, litter, treats - anything from the catalog that doesn't live in a bin. It comes along with your feed delivery.</p>
          )}
          {(data.ride_alongs || []).map((r) => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid #f3f4f6' }}>
              {r.product.image_url && <ZoomImage src={r.product.image_url} size={40} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ overflowWrap: 'anywhere', fontWeight: 600 }}>
                  {r.bag_count} × {r.product.full_name || `${r.product.brand} ${r.product.display_name}`}
                </div>
                <div style={muted}>
                  {r.cadence_label}{r.price ? ` · ${money(r.price)} each` : ''}
                  {!r.available ? ' · not available right now' : r.on_open_order ? ' · on this delivery' : ' · with your next delivery'}
                </div>
              </div>
              <button type="button" style={{ ...secondary, padding: '0 12px' }} disabled={busy} onClick={() => setRideAlong(r)}>Change</button>
              <button type="button" style={{ ...tapLink, background: 'none', border: 'none', padding: '0 4px', color: '#6b7280', fontSize: 14, fontWeight: 600, cursor: 'pointer' }} disabled={busy}
                onClick={() => { if (window.confirm(`Stop bringing ${r.product.full_name || r.product.display_name}?`)) run(() => deliveryAPI.stopRideAlong(r.id)); }}>
                Stop
              </button>
            </div>
          ))}
          <button type="button" style={{ ...secondary, width: '100%', marginTop: 8 }} disabled={busy} onClick={() => setRideAlong(null)}>
            + Also bring something
          </button>
        </div>

        {rideAlong !== undefined && (
          <RideAlongDialog
            existing={rideAlong}
            onClose={() => setRideAlong(undefined)}
            onSaved={(page) => { setData(page); setRideAlong(undefined); }}
          />
        )}

        {changing && (
          <div
            className="modal-overlay"
            onClick={() => { if (!savingFeed) setChanging(null); }}
            // CENTRED, ABOVE THE NAV (Brad, 2026-09-22). As a bottom sheet at z-index 50
            // it sat under the fixed bottom nav and could not scroll into view. The
            // sheet is now a centred dialog stacked above everything, with room left
            // under it for the nav, and it scrolls inside itself.
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 16px 100px' }}
          >
            {/* THREE PARTS, ONLY THE MIDDLE SCROLLS (Brad, 2026-09-22). On a phone the
                whole dialog scrolled, so the buttons sat below a long intro and the
                list, out of reach. Now: a two-line header, the picker in its own
                scrolling area, and the buttons pinned at the bottom of the dialog. */}
            <div
              className="modal-content modal-add-feed"
              onClick={(e) => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: 'calc(100vh - 140px)', padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            >
              {/* A SHORT HEADER (Brad, 2026-09-22): the list is what they came for. */}
              <div style={{ padding: '10px 16px 6px', borderBottom: '1px solid #eee', flex: '0 0 auto' }}>
                <h3 style={{ ...h3, margin: 0, fontSize: 17 }}>{changing.name}: what we deliver</h3>
                <p style={{ ...muted, margin: '2px 0 0', fontSize: 13 }}>
                  From your next delivery on. Nothing is added to the bin now.
                </p>
              </div>
              {/* No scrollbar of its own: the picker fills this space and only its
                  product list scrolls (Brad, 2026-09-22 - two sliders felt odd). */}
              <div style={{ padding: '8px 16px', overflow: 'hidden', flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <FeedProductPicker feedCategory={changing.feed_category || 'dog'} value={feedSel} onChange={setFeedSel} hideAmount fillHeight formFilter />
              </div>
              <div style={{ padding: '10px 16px 14px', borderTop: '1px solid #eee', background: '#fff', flex: '0 0 auto' }}>
                {feedError && <p style={{ ...muted, color: '#b91c1c', fontWeight: 600, margin: '0 0 8px' }}>{feedError}</p>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn-secondary" style={{ flex: 1, minHeight: 44 }} disabled={savingFeed}
                    onClick={() => setChanging(null)}>Cancel</button>
                  <button type="button" className="btn-primary" style={{ flex: 1, minHeight: 44 }}
                    disabled={savingFeed || !hasFeedChoice(feedSel) || feedSel.showOtherInput}
                    onClick={saveFeedChange}>{savingFeed ? 'Saving…' : 'Deliver this instead'}</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Preferences ───────────────────────────────────────────────── */}
        <div style={card}>
          <h3 style={h3}>Delivery day, drop-off and card</h3>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Delivery day</label>
          <select value={data.preferences.preferred_day || ''} disabled={busy}
            onChange={(e) => run(() => deliveryAPI.setDeliveryDay(e.target.value))}
            style={{ minHeight: 44, padding: '0 10px', fontSize: 16, borderRadius: 8, border: '1px solid #d1d5db', marginBottom: 4, width: '100%', maxWidth: 260 }}>
            <option value="" disabled>Choose a day</option>
            {data.preferences.day_choices.map((d) => <option key={d} value={d}>{cap(d)}</option>)}
          </select>
          <p style={{ ...muted, marginTop: 0 }}>A new day takes effect from your next delivery; an order already on the way keeps its date.</p>

          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', margin: '10px 0 4px' }}>Where the driver leaves the bags</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {DROP_OPTIONS.map((v) => {
              const on = data.preferences.drop_location === v;
              return (
                <button key={v} type="button" disabled={savingDrop} onClick={() => saveDrop(v)}
                  style={{ minHeight: 44, padding: '0 14px', borderRadius: 8, border: `1px solid ${on ? RED : '#d1d5db'}`, background: on ? '#fef2f2' : '#fff', color: '#111', fontWeight: on ? 700 : 500, cursor: 'pointer' }}>
                  {DROP_LABEL[v]}
                </button>
              );
            })}
          </div>
          {data.preferences.drop_location === 'garage' && (
            <div style={{ marginTop: 8 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                Garage code{data.preferences.garage_code_set ? ' (saved)' : ''}
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={garageCode} onChange={(e) => setGarageCode(e.target.value)} placeholder={data.preferences.garage_code_set ? '••••' : 'if the door needs one'}
                  style={{ flex: 1, minHeight: 44, padding: '0 10px', fontSize: 16, borderRadius: 8, border: '1px solid #d1d5db', boxSizing: 'border-box' }} />
                <button style={primary} disabled={savingDrop || !garageCode} onClick={() => { saveDrop('garage', garageCode); setGarageCode(''); }}>Save</button>
              </div>
            </div>
          )}
          {data.preferences.drop_location === 'other' && (
            <p style={{ ...muted, marginTop: 6 }}>Add a photo of the spot under <Link to="/bridge-club/profile#delivery-access" style={{ color: RED }}>Delivery access in your Profile</Link> so the driver can find it.</p>
          )}

          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', margin: '12px 0 4px' }}>Card on file</label>
          {data.preferences.card ? (
            <p style={{ margin: 0 }}>
              {data.preferences.card.card_type || 'Card'} ending {data.preferences.card.last_four}, exp {data.preferences.card.expiry}
              {data.preferences.card.expired && <span style={{ color: '#b91c1c', fontWeight: 700 }}> · expired</span>}
              {' '}<Link to="/bridge-club/profile" style={{ ...tapLink, color: RED, fontWeight: 600 }}>Change →</Link>
            </p>
          ) : (
            <p style={{ margin: 0 }}>No card on file. <Link to="/bridge-club/profile" style={{ ...tapLink, color: RED, fontWeight: 600 }}>Add one →</Link></p>
          )}
          <p style={{ ...muted, marginTop: 6 }}>Nothing is charged to your card until your first delivery.</p>
          <p style={{ ...muted, marginTop: 6 }}>Gates, codes, the dog, where things are, photos: <Link to="/bridge-club/profile#delivery-access" style={{ color: RED, fontWeight: 600 }}>Delivery access in your Profile →</Link></p>
        </div>

        {/* ── Punch card ────────────────────────────────────────────────── */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <h3 style={h3}>Free bag punch card</h3>
            <Link to="/bridge-club/card" style={{ ...tapLink, color: RED, fontSize: 14, fontWeight: 600 }}>Your card →</Link>
          </div>
          <p style={{ margin: 0 }}>
            <strong>{data.loyalty.progress} of 10</strong> Red Bridge bags toward your next free bag
            {data.loyalty.reward_available ? <span style={{ color: '#166534', fontWeight: 700 }}> · a free bag is waiting</span> : null}.
          </p>
          <p style={{ ...muted, marginTop: 4 }}>Every Red Bridge bag we deliver counts. Pet food and other brands don't.</p>
        </div>

        {/* ── History ───────────────────────────────────────────────────── */}
        <div style={card}>
          <h3 style={h3}>Past deliveries</h3>
          {data.history.length === 0 && <p style={muted}>No deliveries yet.</p>}
          {data.history.map((h) => (
            <div key={h.id} style={{ padding: '8px 0', borderTop: '1px solid #f3f4f6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                <span>{fmtDay(h.delivery_date)}</span><span>{money(h.total)}</span>
              </div>
              <div style={muted}>{h.lines.map((l) => `${l.bag_count} × ${l.label}`).join(' · ')}</div>
            </div>
          ))}
        </div>
      </div>
    </BridgeClubLayout>
  );
};

export default DeliveryPage;
